
import React, { useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import TennisLogo from './TennisLogo';
import { Users, Calendar, Award, Settings, ClipboardCheck, BarChart2, UserPlus } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuthProfile } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';

import { toast } from 'sonner';


const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, session, profile } = useAuthProfile();
  
  // IMPORTANTE: Todos os hooks devem estar no topo, antes de qualquer return
  const [playerName, setPlayerName] = React.useState<string | null>(null);
  const [playerAvatar, setPlayerAvatar] = React.useState<string | null>(null);

  // Define effectiveProfile para usar nos hooks
  const effectiveProfile = profile || (session ? {
    id: session.user.id,
    role: 'jogador' as const,
    jogador_id: null
  } : null);

  const fetchPlayer = React.useCallback(async () => {
    try {
      const pid = (profile as any)?.jogador_id ? Number((profile as any).jogador_id) : null;
      if (!pid) { setPlayerName(null); setPlayerAvatar(null); return; }
      const { data, error } = await supabase
        .from('jogadores')
        .select('nome, avatar_url')
        .eq('id', pid)
        .maybeSingle();
      if (!error && data) {
        setPlayerName(data.nome ? String(data.nome) : null);
        setPlayerAvatar(data.avatar_url ? String(data.avatar_url) : null);
      } else {
        setPlayerName(null);
        setPlayerAvatar(null);
      }
    } catch {
      setPlayerName(null);
      setPlayerAvatar(null);
    }
  }, [profile]);

  React.useEffect(() => { 
    if (profile) fetchPlayer(); 
  }, [fetchPlayer, profile]);

  React.useEffect(() => {
    const handler = () => { if (profile) fetchPlayer(); };
    window.addEventListener('player-profile-updated', handler);
    return () => window.removeEventListener('player-profile-updated', handler);
  }, [fetchPlayer, profile]);

  const displayName = playerName || (profile as any)?.nome || (profile as any)?.email || 'Usuário';

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = React.useCallback(async () => {
    try {
      await supabase.auth.signOut();
      toast.success('Logout realizado com sucesso!');
      navigate('/auth', { replace: true });
    } catch (error: any) {
      toast.error(error.message || 'Erro ao fazer logout');
      navigate('/auth', { replace: true });
    }
  }, [navigate]);

  const navItems = React.useMemo(() => {
    if (!effectiveProfile) return [];
    if (effectiveProfile.role === 'admin') {
      return [
        { path: '/jogadores', icon: <Users size={18} />, label: 'Jogadores' },
        { path: '/grupos', icon: <Users size={18} />, label: 'Grupos' },
        { path: '/rodadas', icon: <Calendar size={18} />, label: 'Rodadas' },
        { path: '/disponibilidade', icon: <ClipboardCheck size={18} />, label: 'Disponibilidade' },
        { path: '/partidas', icon: <Award size={18} />, label: 'Partidas' },
        { path: '/classificacao', icon: <BarChart2 size={18} />, label: 'Classificação' },
        { path: '/admin', icon: <Settings size={18} />, label: 'Admin' },
      ];
    }

    // role === 'jogador'
    return [
      { path: '/minhas-disponibilidades', icon: <ClipboardCheck size={18} />, label: 'Disponibilidades' },
      { path: '/minhas-partidas', icon: <Award size={18} />, label: 'Minhas Partidas' },
      { path: '/partidas', icon: <Award size={18} />, label: 'Partidas' },
      { path: '/classificacao', icon: <BarChart2 size={18} />, label: 'Classificação' },
    ];
  }, [effectiveProfile]);

  // Returns condicionais após todos os hooks (regra do React)
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!session) {
    return null;
  }
  
  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const getInitials = (name?: string | null, email?: string | null) => {
    const src = (name || email || '').trim();
    if (!src) return 'U';
    const parts = src.split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 w-full border-b bg-card">
        <div className="container flex h-16 items-center justify-between py-4">
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center gap-2">
              <TennisLogo />
            </Link>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-primary ${
                  isActive(item.path) ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                {item.icon}
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="inline-flex items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-primary/50">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={(playerAvatar || (profile as any)?.avatar_url) || undefined} alt={String(displayName)} />
                    <AvatarFallback className="text-xs">
                      {getInitials(playerName || (profile as any)?.nome || null, (profile as any)?.email || null)}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuLabel>
                  {String(displayName)}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    try { 
                      // useNavigate já está disponível no componente
                      navigate('/perfil');
                    } catch {}
                  }}
                >
                  Editar perfil
                </DropdownMenuItem>                
              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); try { handleLogout(); } catch {} }}>Sair</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile navbar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 flex bg-card border-t px-2 py-1">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex-1 flex flex-col items-center justify-center gap-1 py-1 ${
              isActive(item.path) ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            {item.icon}
            <span className="text-[11px] leading-tight text-center">{item.label}</span>
          </Link>
        ))}
      </div>

      {/* Main content */}
      <main className="flex-1 container py-6 md:py-8 pb-20 md:pb-8">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="hidden md:block border-t py-6 md:py-0">
        <div className="container flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <TennisLogo />
            <span className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Sistema de Gestão de Jogos de Tênis
            </span>
          </div>
          <nav className="flex gap-4 text-sm text-muted-foreground">
            <Link to="https://wa.me/+5543996688000" className="hover:underline hover:text-foreground">
              Elaborado por Felipe Fedato Bressan
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
